import { Task } from 'redux-saga';
import { call, cancel, cancelled, fork, put, race, select, take } from 'redux-saga/effects';
import { AssignStreamAction } from 'actions/monitoringActions';
import { getDate } from 'selectors/monitoringSelectors';
import { getTimezoneOffsetCalculator } from 'selectors/serverSelectors';
import { isLoadingTimeline, getTimelineMessages, getTimelineMessagesDate } from 'selectors/timelineSelectors';
import {
    timelineRequest, timelineDataReceived, timelineSuccess, timelineFailure,
    TimelineDataReceivedAction,
} from 'actions/timelineActions';
import { CameraMessage, CellIndex, DeviceId, MaxCells, Timeline } from 'types';
import { splitStreamId } from 'util/streamId';
import { handleErrorSaga } from 'sagas/app/handleError';
import { getTimeline } from 'api/camera';
import { generateTimelineSaga } from './generateTimelineSaga';


export function* assignStreamWatcher() {
    // Allow each cell to have its own task to fetch timeline information
    const cellTasks: Task[] = Array.apply(null, Array(MaxCells)); // stupid syntax, thanks JavaScript.

    while (true) {
        // Wait for a camera stream to be assigned to a cell
        const action: AssignStreamAction = yield take('ASSIGN_STREAM');
        const { cellIndex, streamId } = action.payload;

        // Cancel the existing task for that cell (if there was one)
        if (cellTasks[cellIndex]) {
            yield cancel(cellTasks[cellIndex]);
        }

        // Start a task to fetch the timeline information for this cell
        const { deviceId } = splitStreamId(streamId);
        const utcDate = yield select(getDate);
        cellTasks[cellIndex] = yield fork(timelineRequestSaga, deviceId, cellIndex, utcDate);
    }
}

export function* timelineRequestSaga(deviceId: DeviceId, cellIndex: CellIndex, utcDate: Date) {
    const sameDeviceAndDate = (actionWithMeta: { meta: { deviceId: DeviceId; utcDate: Date | null }}) =>
        actionWithMeta.meta.deviceId === deviceId &&
        (
            actionWithMeta.meta.utcDate === utcDate ||
            !actionWithMeta.meta.utcDate
        )
    ;

    let timeline: Timeline;
    let messages: ReadonlyArray<CameraMessage>;
    try {
        // Are we already fetching the timeline for this device?
        // (recall that a device can have many streams)
        const isLoading: boolean = yield select(isLoadingTimeline, { deviceId });
        const previousUtcTime: Date | null = yield select(getTimelineMessagesDate, { deviceId });
        const isSameDate = !!previousUtcTime && previousUtcTime.toString() === utcDate.toString();

        if (isSameDate && isLoading) {
            // Data is already loading so wait for it to either fail or succeed
            while (true) {
                const { data, failure } = yield race({
                    data: take('TIMELINE_DATA_RECEIVED'),
                    failure: take('TIMELINE_FAILURE'),
                });
                // Bail out if we failed to load the timeline data for the requested device
                if (failure && sameDeviceAndDate(failure)) {
                    return;
                }
                // The data for the requested device was received... so continue with processing
                if (data && sameDeviceAndDate(data)) {
                    messages = (data as TimelineDataReceivedAction).payload;
                    break;
                }
            }
        }
        else if (isSameDate) {
            messages = yield select(getTimelineMessages, { deviceId });
        }
        else {
            const tzOffsetCalculator = yield select(getTimezoneOffsetCalculator);

            // The data is not loaded or in process of loading. So request it.
            // Indicate that we're starting a request... e.g. lets the UI show a loading indicator
            yield put(timelineRequest({ deviceId, utcDate }));

            // Get the camera messages from the server
            messages = yield call(getTimeline, deviceId, utcDate, tzOffsetCalculator);
            yield put(timelineDataReceived(messages, { deviceId, utcDate, append: false }));
        }

        timeline = yield call(generateTimelineSaga, cellIndex, utcDate, messages);
    }
    catch (err) {
        if (yield cancelled()) {
            return;
        }
        yield call(handleErrorSaga, err, timelineFailure, { deviceId, utcDate });
        return;
    }

    yield put(timelineSuccess(timeline, { cellIndex, utcDate }));
}



// WEBPACK FOOTER //
// ./src/sagas/monitoring/timelineRequestSaga.ts